import { PageController } from '@client/components/_core/layout/PageController';
import { EntityDetailsHeader } from '@client/components/_ui/data-display/EntityDetailsHeader';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { SurfaceCard } from '@client/components/_ui/display/SurfaceCard';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { QueryState } from '@client/components/_ui/feedback/QueryState';
import { BackLink } from '@client/components/_ui/navigation/BackLink';
import { exportAttestationsCsv } from '@client/components/attestations/reporting/attestation-export';
import { RoundActivityCard } from '@client/components/attestations/reporting/RoundActivityCard';
import { RoundProgressBar, RoundProgressLegend } from '@client/components/attestations/reporting/RoundProgressBar';
import { RoundRecipientsCard } from '@client/components/attestations/reporting/RoundRecipientsCard';
import {
    type AttestationRound,
    formatRate,
    relativeDayLabel,
    roundStatus,
} from '@client/components/attestations/reporting/reporting-format';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useOrgReportTimezone } from '@client/hooks/business/org/useOrgReportTimezone';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { trpc } from '@client/trpc/client';
import { cn } from '@client/utils/styling';
import { SCHEDULED_USER_ATTESTATION_FREQUENCY_LABELS } from '@shared/constants/business/user-attestation';
import { createFileRoute, notFound, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { Download, IdCard, UserPlus } from 'lucide-react';
import { useState } from 'react';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/console/$orgId/attestations/rounds/$roundId')({
    staticData: {
        metadata: {
            title: 'Attestation round',
        },
        modules: ['attestations'],
        roles: ['org_admin', 'attestations_user'],
        featureFlag: 'enable-attestation-reporting',
        ui: {
            icon: IdCard,
        },
    },
    params: {
        parse({ roundId }) {
            // A malformed id is a 404 here rather than a validation error from the server.
            if (!UUID.test(roundId)) throw notFound();
            return { roundId };
        },
        stringify({ roundId }) {
            return { roundId };
        },
    },
    component: RoundPage,
});

function plural(count: number, noun: string): string {
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function RoundHeader({ round }: { round: AttestationRound }) {
    const { trpcUtils } = useTrpc();
    const timezone = useOrgReportTimezone();
    const { formatDate, formatISODate } = useFormatter();
    const [exporting, setExporting] = useState(false);
    const source =
        round.source === 'SCHEDULED'
            ? `Scheduled${round.schedule?.frequency ? ` · ${SCHEDULED_USER_ATTESTATION_FREQUENCY_LABELS[round.schedule.frequency]}` : ''}`
            : 'Manual send';

    return (
        <EntityDetailsHeader
            icon={<IdCard />}
            title={round.form?.formTitle ?? 'Attestation round'}
            titleAddon={
                <span className="flex items-center gap-2">
                    {round.form && (
                        <Tag variant="outlined" color="default" className="m-0 font-mono text-xs">
                            v{round.form.version}
                        </Tag>
                    )}
                    <StatusBadge status={roundStatus(round)} module="attestationRound" variant="filled" />
                </span>
            }
            subtitle={[
                source,
                round.Sender && `sent by ${round.Sender.fullName}`,
                plural(round.counts.total, 'recipient'),
            ]
                .filter(Boolean)
                .join(' · ')}
            rightAddon={
                <Button
                    type="secondary"
                    icon={<Download />}
                    loading={exporting}
                    onClick={async () => {
                        setExporting(true);
                        await exportAttestationsCsv(
                            trpcUtils.client,
                            { where: { roundId: round.roundId }, orderBy: { dueAt: 'asc' } },
                            'attestation-round',
                            timezone,
                        );
                        setExporting(false);
                    }}
                >
                    Export
                </Button>
            }
            mainInfo={[
                { title: 'Sent', children: formatDate(round.sentAt) },
                {
                    title: 'Due',
                    children: (
                        <span>
                            {formatISODate(round.dueDate)}
                            {round.dueInDays !== null && (
                                <span className={cn('ml-1', round.dueInDays < 0 && 'text-danger-foreground')}>
                                    · {relativeDayLabel(round.dueInDays)}
                                </span>
                            )}
                            {round.latestDueDate && (
                                <span className="ml-1 text-subtle-foreground">
                                    · some extended to {formatISODate(round.latestDueDate)}
                                </span>
                            )}
                        </span>
                    ),
                },
                { title: 'Late submission', children: round.allowOverdueSubmission ? 'Allowed' : 'Not allowed' },
                {
                    title: 'Completion',
                    children: `${formatRate(round.completionRate)} · ${round.submitted} of ${round.expected}`,
                },
                {
                    title: 'On time',
                    children: `${formatRate(round.onTimeRate)} · ${round.submitted - round.counts.late} of ${round.submitted}`,
                },
                {
                    title: 'Median time to submit',
                    children: round.medianDaysToSubmit === null ? '—' : `${round.medianDaysToSubmit.toFixed(1)} days`,
                },
            ]}
            bottomAddon={
                <div className="flex flex-col gap-2">
                    <RoundProgressBar counts={round.counts} size="large" />
                    <RoundProgressLegend counts={round.counts} />
                </div>
            }
        />
    );
}

function RoundContent({ round }: { round: AttestationRound }) {
    const orgId = useOrgId();
    return (
        <>
            <RoundHeader round={round} />
            <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
                <RoundRecipientsCard round={round} />
                <div className="flex flex-col gap-4">
                    <RoundActivityCard activity={round.activity} />
                    <SurfaceCard icon={UserPlus} title="Coverage">
                        <p className="m-0 text-muted-foreground text-sm">
                            Check which active members have not been sent this form this year, such as people who joined
                            after this round went out, and send it to them.
                        </p>
                        <div>
                            <Button.Link
                                to="/console/$orgId/attestations/rounds/$roundId/coverage"
                                params={{ orgId, roundId: round.roundId }}
                                variant="secondary"
                                size="small"
                            >
                                Check coverage
                            </Button.Link>
                        </div>
                    </SurfaceCard>
                </div>
            </div>
        </>
    );
}

function RoundPage() {
    const orgId = useOrgId();
    const { roundId } = Route.useParams();
    const query = trpc.userAttestation.getAttestationRound.useQuery({ roundId }, { meta: { errorSurface: 'inline' } });

    return (
        <PageController route={Route}>
            <div className="flex flex-col gap-4">
                <BackLink to="/console/$orgId/attestations/overview" params={{ orgId }}>
                    Back to overview
                </BackLink>
                <QueryState query={query} loading={<Loading />} errorTitle="This round could not be loaded">
                    {(round) => <RoundContent round={round} />}
                </QueryState>
            </div>
            <Outlet />
        </PageController>
    );
}
